import { EconomicEventOutput } from '@api/api/financial-events/economics-events';
import { createScenarioRequest } from '@terminal/react-query-hooks/scenarios/useScenarios.utils';
import { SelectedAssetItem } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/run-scenario/RunScenario';
import { addAssetFilterOptions } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/run-scenario/utils/utils';
import { ConditionOptions } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/getConditionOptions';
import { useScenarioAssistant } from '@terminal/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { mapAssistantConditionsToConditions } from '@terminal/widgets/scenario/utils/scenario-conditions/scenario-conditions';
import { useTranslation } from 'react-i18next';

import { useFilterActions } from '../use-filter-actions/useFilterActions';
import { createCustomCondition, resolveSelectedAssetEntityTags } from './utils';

export type UseRunScenarioProps = {
  data: EconomicEventOutput;
  selectedAssets?: SelectedAssetItem;
  customConditionOptions?: ConditionOptions;
};

export const useRunScenario = ({
  data,
  selectedAssets,
  customConditionOptions,
}: UseRunScenarioProps) => {
  const { t } = useTranslation(['scenario']);
  const {
    addFilterOption: addAssetFilterOption,
    resetActiveFilterOptions: resetActiveAssetFilterOptions,
  } = useFilterActions({
    store: useScenarioStore,
    group: 'ASSET_FILTERS',
  });
  const { resetActiveFilterOptions: resetActiveConditionFilterOptions } =
    useFilterActions({
      store: useScenarioStore,
      group: 'SCENARIO_CONDITIONS',
    });
  const { manuallyAddCustomCondition, resetConditions } = useScenarioAssistant(
    state => ({
      manuallyAddCustomCondition: state.manuallyAddCustomCondition,
      resetConditions: state.resetConditions,
    })
  );

  const initializeAssetFilterOptions = () => {
    resetActiveAssetFilterOptions();
    addAssetFilterOptions({
      selectedAssets,
      addAssetFilterOption,
    });
  };

  const initializeConditionFilterOptions = async () => {
    if (!customConditionOptions) {
      return;
    }

    resetActiveConditionFilterOptions();
    resetConditions();

    const customCondition = createCustomCondition(
      customConditionOptions,
      data.country,
      data.actual_unit
    );

    await manuallyAddCustomCondition(t, customCondition);
  };

  const prepareScenarioRun = async () => {
    initializeAssetFilterOptions();
    await initializeConditionFilterOptions();

    if (!selectedAssets) {
      return;
    }

    const entityTags = await resolveSelectedAssetEntityTags(selectedAssets);
    const mappedConditions = mapAssistantConditionsToConditions(
      useScenarioAssistant.getState().conditions.filter(c => c.active)
    );
    const { setPendingAutoRunRequest, setScenarioPageEntityTags } =
      useScenarioStore.getState();

    setScenarioPageEntityTags(entityTags);

    if (mappedConditions.length && entityTags.length) {
      setPendingAutoRunRequest(
        createScenarioRequest({
          conditions: mappedConditions,
          entityTags,
        })
      );
    }
  };

  return {
    initializeAssetFilterOptions,
    initializeConditionFilterOptions,
    prepareScenarioRun,
  };
};
